export interface Instructor {
  id: number;
  name: string;
  description: string;
  linkImage: string;
  layout: string;
  backgroundImage: string;
  backgroundImageMobile: string;
  createdAt: string;
  updatedAt: string;
}

export interface PublishProgramPayload {
  year: number;
  month: number;
  date: number;
  hours: number;
  minutes: number;
}

export interface Option {
  id: number;
  name: string;
}

export interface DetailsOption<T extends string | number | boolean> {
  value: T;
  label: string;
}

export interface InitialStepInputs {
  availableTypes: Option[];
}

export interface ConfigurationStepInputs {
  availableCategories: Option[];
  availableLevels: Option[];
  availableObjectives: Option[];
  availableModalities: Option[];
  availableTags: Option[];
}

interface ProgramModule {
  id: number;
  title: string;
  order: number;
  description: string;
  subModules: any[];
  contents: ProgramContent[];
}

interface ProgramContent {
  id: number;
  title: string;
  description: string;
  type: string;
  directory: string;
  value: string;
  order: number;
  tags: string[];
  groupId: number | null;
  instructors: Option[];
  thumbVideo: {
    neutral?: string;
    female?: string;
    male?: string;
  };
}

interface ProgramFeature {
  id?: number;
  name: string;
  value: string | string[] | boolean | number;
}

export interface ProgramAdditionalInformation {
  id?: number;
  name: string;
  description: string;
  activated: boolean;
  order: number;
  type: string;
}

export interface ProgramImages {
  thumb: {
    web: {
      neutral: string | null;
      female: string | null;
      male: string | null;
    };
    mobile: {
      neutral: string | null;
      female: string | null;
      male: string | null;
    };
    tv: {
      neutral: string | null;
      female: string | null;
      male: string | null;
    };
  };
  logotipo: {
    neutral: string | null;
    female: string | null;
    male: string | null;
  };
  bannerInterno: {
    web: {
      neutral: string | null;
      female: string | null;
      male: string | null;
    };
    tv: {
      neutral: string | null;
      female: string | null;
      male: string | null;
    };
    mobileApp: {
      neutral: string | null;
      female: string | null;
      male: string | null;
    };
    tablet: {
      neutral: string | null;
      female: string | null;
      male: string | null;
    };
  };
  bannerQuiz: {
    web: {
      neutral: string | null;
      female: string | null;
      male: string | null;
    };
    appResponsive: {
      neutral: string | null;
      female: string | null;
      male: string | null;
    };
  };
}

export interface Program {
  id: number;
  name: string;
  description: string;
  teaser: string;
  status: string;
  releaseDate: string | null;
  averageContentTimeInMinutes: number;
  totalDurationInDays: number;
  instructors: Option[];
  typesProgram: Option[];
  objectives: Option[];
  levels: Option[];
  modalities: Option[];
  tags: string[];
  categories: Option[];
  features: ProgramFeature[];
  modules: ProgramModule[];
  additionalInformation: ProgramAdditionalInformation[];
  segments: string;
  images: ProgramImages;
}

export interface ProgramContentPayload {
  id?: number;
  title: string;
  description: string;
  type: string;
  directory: string;
  value: string;
  order: number;
  instructorIds: number[];
  thumbVideo: {
    neutral?: string;
    female?: string;
    male?: string;
  };
}

export interface ProgramModulePayload {
  id?: number;
  title: string;
  order: number;
  description: string;
  contents: ProgramContentPayload[];
}

export interface CreateOrUpdateProgramPayload {
  id?: number;
  name: string;
  description: string;
  status: string;
  teaser: string;
  releaseDate: string;
  averageContentTimeInMinutes: number;
  totalDurationInDays: number;
  instructorIds: number[] | null;
  typeIds: number[];
  objectives: { id: number; order: number }[];
  levelIds: number[];
  modalityIds: number[];
  tagNames: string[];
  categoryIds: number[];
  segmentId: number;
  features: ProgramFeature[];
  modules: ProgramModulePayload[];
  additionalInformation: ProgramAdditionalInformation[];
}

export enum ProgramFeatureName {
  IsRecommendQuiz = 'isRecommendedInQuiz',
  ShowInSearch = 'showInSearch',
  HasAccessory = 'hasAccessory',
  TrainingDaysAvailable = 'trainingDaysAvailable',
  ForMother = 'forMother',
  DurationProgram = 'durationProgram',
  Gender = 'gender',
}

export enum GenderOptions {
  Female = 'F',
  Male = 'M',
  Neutral = 'I',
}

export const genderMapping: Record<
  GenderOptions,
  'female' | 'male' | 'neutral'
> = {
  [GenderOptions.Female]: 'female',
  [GenderOptions.Male]: 'male',
  [GenderOptions.Neutral]: 'neutral',
};

export enum ProgramStatus {
  Active = 'active',
  Inactive = 'inactive',
  Unpublished = 'unpublished',
  Incompleted = 'incompleted',
  Scheduled = 'scheduled',
  Completed = 'completed',
}

export interface SpallaInfo {
  assets: {
    iframe: string;
    player: string;
    thumbnail: string;
    endScreen: string;
    mp4: {
      '240p': string;
      '360p': string;
      '480p': string;
      '720p': string;
    };
    hls: string;
    vtt: string;
    highlights: boolean;
    thumbPreview25: string;
    thumbPreview50: string;
    thumbPreview75: string;
  };
}
